import { useState, type SubmitEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { format, formatDistanceToNow } from 'date-fns';
import {
  AlertCircle,
  Archive,
  Ban,
  Bell,
  BellOff,
  Bot,
  Check,
  CheckCheck,
  ChevronLeft,
  Clock,
  Eye,
  Link2,
  Link2Off,
  MessageSquare,
  Pause,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
  Send,
  Server,
  SkipForward,
  Undo2,
  UserCheck,
  UserPlus,
  X,
} from 'lucide-react';
import {
  type CMEvent,
  type CMState,
  useAddCMComment,
  useApproveCM,
  useArchiveCM,
  useCMAffectedDevices,
  useCMAlerts,
  useCMConflicts,
  useCMSettings,
  useSubmitCM,
  useAttachPlan,
  useCMEvents,
  useCMPlans,
  useCancelCM,
  useChangeManagement,
  useCompleteCM,
  useDetachPlan,
  usePauseCM,
  useRejectCM,
  useResumeCM,
  useRetryCM,
  useStartCM,
  useUpdateCM,
} from '@/hooks/useChangeManagements';
import { useCreatePlan, useNetworkPlans } from '@/hooks/useNetworkPlans';
import { useAuth } from '@/hooks/useAuth';
import { useConfirm, type ConfirmOptions } from '@/hooks/useConfirm';
import { useAvailableContexts } from '@/hooks/useContexts';
import ParticipantsPanel from '@/components/ParticipantsPanel';
import PlanStepEditor from '@/components/PlanStepEditor';
import CMSnapshotsPanel from '@/components/CMSnapshotsPanel';
import CMLifecycleStepper from '@/components/CMLifecycleStepper';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import { Checkbox } from '@/components/ui/checkbox';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { AppSheet } from '@/components/ui/app-sheet';
import { Progress } from '@/components/ui/progress';
import { PageContainer } from '@/components/ui/page-container';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { apiErrorMessage } from '@/utils/apiError';
import { deviceTypeLabel } from '@/types/deviceType';
import { describeRollback } from '@/utils/describeRollback';

/**
 * CMDetails — single change-management page. Spec §4 MS.
 *
 * Largest single migration this phase (879 lines). Layout:
 *   • Header: state + automated/suppressed/archived badges + Edit/Archive
 *   • 2/3 main column:
 *       - Details dl (context, description, dates, approval status,
 *         automation source)
 *       - Implementation: attached plans rendered inline via
 *         <PlanStepEditor> (build steps while draft, run manual steps
 *         while executing) + add/attach plan while draft
 *       - Workflow actions row (state-driven: submit/approve/reject/
 *         start/pause/resume/complete/cancel)
 *       - Activity timeline (events) with inline add-comment form
 *   • 1/3 sidecar: ParticipantsPanel (still Bulma — migrates later)
 *
 * Two AppSheets: Edit + Attach plan. ParticipantsPanel stays Bulma
 * for this commit — it has its own Add-participant modal that needs
 * its own treatment.
 */

const STATE_STYLE: Record<CMState, string> = {
  draft: 'bg-muted text-muted-foreground',
  waiting_approval: 'bg-sev-3/20 text-foreground',
  approved: 'bg-sev-5/15 text-sev-5',
  rejected: 'bg-sev-1/15 text-sev-1',
  in_progress: 'bg-brand-soft text-brand',
  paused: 'bg-sev-3/30 text-foreground',
  verification: 'bg-sev-5/20 text-sev-5',
  completed: 'bg-sev-4/15 text-sev-4',
  cancelled: 'bg-muted text-muted-foreground',
  partially_completed: 'bg-sev-3/20 text-foreground',
};

const PLAN_STATE_STYLE: Record<string, string> = {
  draft: 'bg-muted text-muted-foreground',
  locked: 'bg-sev-3/20 text-foreground',
  executing: 'bg-brand-soft text-brand',
  completed: 'bg-sev-4/15 text-sev-4',
  failed: 'bg-sev-1/15 text-sev-1',
  cancelled: 'bg-sev-3/30 text-foreground',
};

const ALERT_REL_STYLE: Record<string, string> = {
  suppressed: 'bg-muted text-muted-foreground',
  unexpected: 'bg-sev-1/15 text-sev-1',
  expected: 'bg-sev-4/15 text-sev-4',
  remediates: 'bg-brand-soft text-brand',
};

function formatState(state: string): string {
  return state
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

function eventIcon(eventType: string) {
  switch (eventType) {
    case 'comment': return MessageSquare;
    case 'state_changed': return RotateCcw;
    case 'approved': return Check;
    case 'rejected': return X;
    case 'started': return Play;
    case 'paused': return Pause;
    case 'resumed': return Play;
    case 'completed': return CheckCheck;
    case 'cancelled': return Ban;
    case 'owner_added': return UserPlus;
    case 'approver_added': return UserCheck;
    case 'watcher_added': return Eye;
    case 'assignee_added': return UserPlus;
    case 'plan_attached': return Link2;
    case 'plan_detached': return Link2Off;
    case 'plan_retried': return RefreshCw;
    case 'retried': return RefreshCw;
    case 'step_completed': return Check;
    case 'step_failed': return X;
    case 'step_skipped': return SkipForward;
    case 'plan_completed': return CheckCheck;
    case 'plan_failed': return X;
    case 'auto_start_missed': return Clock;
    case 'auto_start_held': return AlertCircle;
    case 'started_without_approval': return AlertCircle;
    case 'rolled_back': return Undo2;
    case 'remediation_blocked': return ShieldAlert;
    default: return Bell;
  }
}

interface EventDescription {
  title: string;
  detail?: string;
}

function metaStr(m: Record<string, unknown>, key: string): string | undefined {
  const v = m[key];
  return typeof v === 'string' && v.length > 0 ? v : undefined;
}

// "Added Alice as approver" when the target is resolved, else "Added approver".
function roleText(verb: 'Added' | 'Removed', name: string | undefined, role: string): string {
  return name ? `${verb} ${name} as ${role}` : `${verb} ${role}`;
}

// Human-readable description of a timeline event — replaces dumping raw metadata JSON.
function describeCMEvent(event: CMEvent): EventDescription {
  const m = event.metadata ?? {};
  const planTitle = metaStr(m, 'planTitle');
  const target = metaStr(m, 'targetName');

  switch (event.eventType) {
    case 'comment':
      return { title: 'Commented', detail: event.content ?? undefined };
    case 'state_changed': {
      if (m.action === 'updated') return { title: 'Edited the details' };
      if (m.action === 'archived') return { title: 'Archived the change' };
      const from = metaStr(m, 'from');
      const to = metaStr(m, 'to');
      if (from && to) return { title: 'Status changed', detail: `${formatState(from)} → ${formatState(to)}` };
      if (to) return { title: 'Created the change', detail: `Status: ${formatState(to)}` };
      return { title: 'Status changed' };
    }
    case 'approved': return { title: 'Approved the change' };
    case 'rejected': return { title: 'Rejected the change' };
    case 'started': return { title: 'Started execution' };
    case 'paused': return { title: 'Paused execution' };
    case 'resumed': return { title: 'Resumed execution' };
    case 'completed': return { title: 'Marked the change complete' };
    case 'cancelled': return { title: 'Cancelled the change' };
    case 'started_without_approval': return { title: 'Started without full approval' };
    case 'rolled_back': return describeRollback(m);
    case 'auto_start_held': {
      const problems = Array.isArray(m.problems) ? m.problems.filter((p): p is string => typeof p === 'string') : [];
      return {
        title: 'Held its scheduled auto-start: a step can\'t run as it stands',
        detail: problems.length > 0 ? problems.join('\n') : undefined,
      };
    }
    case 'auto_start_missed': {
      const st = metaStr(m, 'startTime');
      return {
        title: 'Missed its scheduled auto-start',
        detail: st ? `Was scheduled for ${new Date(st).toLocaleString()}` : 'Was not approved in time',
      };
    }
    case 'owner_added': return { title: roleText('Added', target, 'owner') };
    case 'owner_removed': return { title: roleText('Removed', target, 'owner') };
    case 'approver_added': return { title: roleText('Added', target, 'approver') };
    case 'approver_removed': return { title: roleText('Removed', target, 'approver') };
    case 'watcher_added': return { title: roleText('Added', target, 'watcher') };
    case 'watcher_removed': return { title: roleText('Removed', target, 'watcher') };
    case 'assignee_added': return { title: roleText('Added', target, 'assignee') };
    case 'assignee_removed': return { title: roleText('Removed', target, 'assignee') };
    case 'plan_attached': return { title: 'Attached a plan', detail: planTitle };
    case 'plan_detached': return { title: 'Detached a plan', detail: planTitle };
    case 'plan_retried': return { title: 'Retried a plan' };
    case 'retried': {
      const ids = m.retriedPlanIds;
      const n = Array.isArray(ids) ? ids.length : 0;
      return { title: 'Retried failed plans', detail: n ? `${String(n)} plan${n === 1 ? '' : 's'}` : undefined };
    }
    case 'step_completed': return { title: 'Completed a step', detail: event.content ?? undefined };
    case 'step_failed': return { title: 'A step failed', detail: event.content ?? undefined };
    case 'step_skipped': return { title: 'Skipped a step', detail: event.content ?? undefined };
    case 'plan_completed': return { title: 'Plan completed', detail: planTitle ?? event.content ?? undefined };
    case 'plan_failed': return { title: 'Plan failed', detail: planTitle ?? event.content ?? undefined };
    case 'device_added': return { title: 'Added an affected device' };
    case 'device_removed': return { title: 'Removed an affected device' };
    case 'remediation_blocked':
      return { title: 'A scripted fix could not be added', detail: event.content ?? undefined };
    default: return { title: formatState(event.eventType) };
  }
}

export default function CMDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editSuppressAlerts, setEditSuppressAlerts] = useState(false);
  const [editStartTime, setEditStartTime] = useState('');
  const [editExpectedEndTime, setEditExpectedEndTime] = useState('');
  const [editAutoStart, setEditAutoStart] = useState(false);

  const [commentText, setCommentText] = useState('');
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { confirm, confirmDialog } = useConfirm();
  const [isAttachOpen, setIsAttachOpen] = useState(false);
  const [attachPlanId, setAttachPlanId] = useState('');

  const { data: cm, isLoading, isError, error } = useChangeManagement(id);
  const { data: events, isLoading: eventsLoading } = useCMEvents(id);
  const { data: contexts } = useAvailableContexts(cm?.organizationId);
  const { data: cmPlans } = useCMPlans(id);
  const { data: affectedDevices } = useCMAffectedDevices(id);
  const { data: cmAlerts } = useCMAlerts(id);
  const { data: conflicts } = useCMConflicts(id);
  const { data: cmSettings } = useCMSettings();
  const { data: availablePlans } = useNetworkPlans(user?.organizationId, { state: 'draft' });

  const submitMutation = useSubmitCM();
  const approveMutation = useApproveCM();
  const rejectMutation = useRejectCM();
  const startMutation = useStartCM();
  const pauseMutation = usePauseCM();
  const resumeMutation = useResumeCM();
  const completeMutation = useCompleteCM();
  const cancelMutation = useCancelCM();
  const archiveMutation = useArchiveCM();
  const updateMutation = useUpdateCM();
  const addCommentMutation = useAddCMComment();
  const attachPlanMutation = useAttachPlan();
  const detachPlanMutation = useDetachPlan();
  const createPlanMutation = useCreatePlan();
  const retryCMMutation = useRetryCM();

  const runAction = async (
    mutationFn: (cmId: string) => Promise<unknown>,
    confirmOptions: ConfirmOptions | null,
    failMessage: string,
  ) => {
    if (!id) return;
    if (confirmOptions && !(await confirm(confirmOptions))) return;
    try {
      await mutationFn(id);
      setSubmitError(null);
    } catch (err) {
      setSubmitError(apiErrorMessage(err, failMessage));
    }
  };

  const handleSubmit = () => runAction(
    (i) => submitMutation.mutateAsync(i),
    {
      title: 'Submit for approval',
      message: 'Submit this change for approval? Attached plans will be locked for review.',
      confirmText: 'Submit',
    },
    'Failed to submit change management for approval.',
  );
  const handleApprove = () => runAction(
    (i) => approveMutation.mutateAsync(i),
    null,
    'Failed to approve change management.',
  );
  const handleReject = () => runAction(
    (i) => rejectMutation.mutateAsync(i),
    {
      title: 'Reject change',
      message: 'Reject this change management?',
      confirmText: 'Reject',
      isDanger: true,
    },
    'Failed to reject change management.',
  );
  const handleStart = () => runAction(
    (i) => startMutation.mutateAsync(i),
    null,
    'Failed to start change management.',
  );
  // Operator override: start a change that hasn't been fully approved.
  const handleStartUnapproved = () => runAction(
    (i) => startMutation.mutateAsync(i),
    {
      title: 'Start without approval',
      message: 'This change has not been fully approved. Start it anyway?',
      confirmText: 'Start anyway',
      isDanger: true,
    },
    'Failed to start change management.',
  );
  const handlePause = () => runAction(
    (i) => pauseMutation.mutateAsync(i),
    null,
    'Failed to pause change management.',
  );
  const handleResume = () => runAction(
    (i) => resumeMutation.mutateAsync(i),
    null,
    'Failed to resume change management.',
  );
  const handleComplete = () => {
    // Completion gate: warn if unexpected alerts are still active on affected devices.
    const stillActive = (cmAlerts ?? []).filter(
      (a) =>
        a.relationship === 'unexpected' &&
        (a.incidentStatus === 'created' || a.incidentStatus === 'acknowledged'),
    );
    const message =
      stillActive.length > 0
        ? `${String(stillActive.length)} unexpected alert${stillActive.length === 1 ? '' : 's'} ${
            stillActive.length === 1 ? 'is' : 'are'
          } still active on this change's devices. Complete anyway?`
        : 'Mark this change management as completed?';
    return runAction(
      (i) => completeMutation.mutateAsync(i),
      {
        title: 'Complete change',
        message,
        confirmText: 'Complete',
        isDanger: stillActive.length > 0,
      },
      'Failed to complete change management.',
    );
  };
  const handleCancel = () => runAction(
    (i) => cancelMutation.mutateAsync(i),
    {
      title: 'Cancel change',
      message: 'Cancel this change management?',
      confirmText: 'Cancel change',
      cancelText: 'Keep it',
      isDanger: true,
    },
    'Failed to cancel change management.',
  );
  const handleRetryFailedPlans = () => runAction(
    (i) => retryCMMutation.mutateAsync(i),
    {
      title: 'Retry failed plans',
      message: 'Retry all failed plans in this change management?',
      confirmText: 'Retry',
    },
    'Failed to retry plans.',
  );

  const handleArchive = async () => {
    if (!id) return;
    const ok = await confirm({
      title: 'Archive change',
      message: 'Archive this change management?',
      confirmText: 'Archive',
      isDanger: true,
    });
    if (!ok) return;
    try {
      await archiveMutation.mutateAsync(id);
      void navigate('/change-management');
    } catch (err) {
      // The backend says why: an owner may archive only until the change starts, and
      // after that it takes cm:delete.
      setSubmitError(apiErrorMessage(err, 'Failed to archive change management.'));
    }
  };

  const handleAddComment = async (e: SubmitEvent) => {
    e.preventDefault();
    if (!id || !commentText.trim()) return;
    try {
      await addCommentMutation.mutateAsync({ cmId: id, data: { content: commentText } });
      setCommentText('');
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to add comment.');
    }
  };

  const handleAttachPlan = async (e?: SubmitEvent) => {
    e?.preventDefault();
    if (!id || !attachPlanId) return;
    try {
      await attachPlanMutation.mutateAsync({ cmId: id, data: { planId: attachPlanId } });
      setAttachPlanId('');
      setIsAttachOpen(false);
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to attach plan.');
    }
  };

  const handleDetachPlan = async (planId: string) => {
    if (!id) return;
    const ok = await confirm({
      title: 'Detach plan',
      message: 'Detach this plan from the CM?',
      confirmText: 'Detach',
    });
    if (!ok) return;
    try {
      await detachPlanMutation.mutateAsync({ cmId: id, planId });
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to detach plan.');
    }
  };

  // Create a fresh draft plan and attach it in one step, so the user builds the
  // implementation inline on the CM without ever visiting the plans page.
  const handleAddImplementationPlan = async () => {
    if (!id || !cm) return;
    try {
      const plan = await createPlanMutation.mutateAsync({
        contextId: cm.contextId,
        title: `${cm.title} — implementation`,
      });
      await attachPlanMutation.mutateAsync({ cmId: id, data: { planId: plan.id } });
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to add an implementation plan.');
    }
  };

  const openEditModal = () => {
    if (!cm) return;
    setEditTitle(cm.title);
    setEditDescription(cm.description ?? '');
    setEditSuppressAlerts(cm.suppressAlerts);
    setEditStartTime(cm.startTime ? cm.startTime.substring(0, 16) : '');
    setEditExpectedEndTime(cm.expectedEndTime ? cm.expectedEndTime.substring(0, 16) : '');
    setEditAutoStart(cm.autoStart);
    setIsEditOpen(true);
  };

  const handleEditSubmit = async (e?: SubmitEvent) => {
    e?.preventDefault();
    if (!id) return;
    try {
      await updateMutation.mutateAsync({
        cmId: id,
        data: {
          title: editTitle,
          description: editDescription,
          suppressAlerts: editSuppressAlerts,
          startTime: editStartTime || undefined,
          expectedEndTime: editExpectedEndTime || undefined,
          autoStart: editAutoStart,
        },
      });
      setIsEditOpen(false);
      setSubmitError(null);
    } catch {
      setSubmitError('Failed to update change management.');
    }
  };

  if (isLoading) {
    return (
      <PageContainer>
        <Skeleton className="h-6 w-48 mb-3" />
        <Skeleton className="h-9 w-80 mb-2" />
        <Skeleton className="h-4 w-32 mb-6" />
        <Skeleton className="h-64 w-full" />
      </PageContainer>
    );
  }

  if (isError || !cm) {
    return (
      <PageContainer>
        <Alert variant="destructive" className="mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Error loading change management:{' '}
            {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
        <Button variant="outline" asChild>
          <Link to="/change-management">
            <ChevronLeft className="w-4 h-4" />
            Back to change management
          </Link>
        </Button>
      </PageContainer>
    );
  }

  const context = contexts?.find((ctx) => ctx.id === cm.contextId);

  // State-driven workflow buttons. Spec §4 design-stakes #2: the
  // multi-phase CM ticket flow is the most differentiated UI.
  interface WorkflowAction {
    label: string;
    onClick: () => void;
    variant: 'default' | 'outline';
    Icon: typeof Check;
    /** Tint for primary actions — uses sev tokens. */
    className?: string;
  }
  // A running change can't be archived (website-backend#89): it would leave the list
  // while it is still touching devices.
  const isRunning = cm.state === 'in_progress' || cm.state === 'paused' || cm.state === 'verification';
  const workflowActions: WorkflowAction[] = (() => {
    switch (cm.state) {
      case 'draft':
        return [
          { label: 'Submit for approval', onClick: () => { void handleSubmit(); }, variant: 'default', Icon: Send },
          { label: 'Cancel', onClick: () => { void handleCancel(); }, variant: 'outline', Icon: Ban },
        ];
      case 'waiting_approval':
        return [
          { label: 'Approve', onClick: () => { void handleApprove(); }, variant: 'default', Icon: Check, className: 'bg-sev-4 hover:bg-sev-4/90 text-white' },
          { label: 'Reject', onClick: () => { void handleReject(); }, variant: 'outline', Icon: X, className: 'text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10' },
          { label: 'Start anyway', onClick: () => { void handleStartUnapproved(); }, variant: 'outline', Icon: Play },
          { label: 'Cancel', onClick: () => { void handleCancel(); }, variant: 'outline', Icon: Ban },
        ];
      case 'approved':
        return [
          { label: 'Start', onClick: () => { void handleStart(); }, variant: 'default', Icon: Play },
          { label: 'Cancel', onClick: () => { void handleCancel(); }, variant: 'outline', Icon: Ban },
        ];
      case 'in_progress':
        return [
          { label: 'Pause', onClick: () => { void handlePause(); }, variant: 'outline', Icon: Pause },
          { label: 'Complete', onClick: () => { void handleComplete(); }, variant: 'default', Icon: CheckCheck, className: 'bg-sev-4 hover:bg-sev-4/90 text-white' },
          { label: 'Cancel', onClick: () => { void handleCancel(); }, variant: 'outline', Icon: Ban },
        ];
      case 'paused':
        return [
          { label: 'Resume', onClick: () => { void handleResume(); }, variant: 'default', Icon: Play },
          { label: 'Cancel', onClick: () => { void handleCancel(); }, variant: 'outline', Icon: Ban },
        ];
      // No Cancel: the changes are already applied, and cancelling would undo nothing.
      // Rollback is the way back from here.
      case 'verification':
        return [
          { label: 'Complete', onClick: () => { void handleComplete(); }, variant: 'default', Icon: CheckCheck, className: 'bg-sev-4 hover:bg-sev-4/90 text-white' },
        ];
      default:
        return [];
    }
  })();

  // Aggregate step progress across every attached plan, for the execution summary.
  const isCmExecuting =
    cm.state === 'in_progress' || cm.state === 'paused' || cm.state === 'verification';
  const totalSteps = cmPlans?.reduce((sum, p) => sum + p.stepCount, 0) ?? 0;
  const completedSteps = cmPlans?.reduce((sum, p) => sum + p.completedStepCount, 0) ?? 0;

  return (
    <PageContainer>
      <nav
        className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground mb-3"
        aria-label="Breadcrumb"
      >
        <Link to="/change-management" className="hover:text-foreground">Change management</Link>
        <ChevronLeft className="w-3 h-3 rotate-180" />
        <span className="text-foreground truncate">{cm.title}</span>
      </nav>
      <Link
        to="/change-management"
        className="md:hidden inline-flex items-center gap-1.5 text-sm text-muted-foreground mb-3 -ml-1"
      >
        <ChevronLeft className="w-4 h-4" />
        All changes
      </Link>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 md:gap-4 mb-5">
        <div className="min-w-0 flex-1">
          <h1 className="text-xl md:text-2xl font-semibold tracking-tight break-words">
            {cm.title}
          </h1>
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            <Badge data-testid="cm-state" className={cn('border-0 font-normal', STATE_STYLE[cm.state])}>
              {formatState(cm.state)}
            </Badge>
            {cm.changeType !== 'normal' ? (
              <Badge variant="outline" className="font-normal capitalize">{cm.changeType}</Badge>
            ) : null}
            {cm.riskLevel ? (
              <Badge
                variant="outline"
                className={cn(
                  'font-normal capitalize',
                  cm.riskLevel === 'high' && 'border-sev-3/50 text-sev-3',
                )}
              >
                {cm.riskLevel} risk
              </Badge>
            ) : null}
            {cm.isAutomated ? (
              <Badge variant="secondary" className="font-normal">
                <Bot className="w-3 h-3" />
                Automated
              </Badge>
            ) : null}
            {cm.suppressAlerts ? (
              <Badge className="bg-sev-3/20 text-foreground border-0 font-normal">
                <BellOff className="w-3 h-3" />
                Alerts suppressed
              </Badge>
            ) : null}
            {cm.archivedAt ? (
              <Badge variant="outline" className="text-muted-foreground">
                <Archive className="w-3 h-3" />
                Archived
              </Badge>
            ) : null}
          </div>
        </div>
        {!cm.archivedAt ? (
          <div className="flex items-center gap-2 flex-shrink-0">
            <Button variant="outline" size="sm" onClick={openEditModal}>
              <Pencil className="w-4 h-4" />
              Edit
            </Button>
            {!isRunning ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => { void handleArchive(); }}
              className="text-destructive hover:text-destructive border-destructive/30 hover:bg-destructive/10"
            >
              <Archive className="w-4 h-4" />
              Archive
            </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      {/* Lifecycle stepper — automated CMs skip the draft/approval path, so the
          badge alone tells their story. */}
      {!cm.isAutomated ? (
        <div className="bg-card border border-border rounded-lg px-5 py-3 mb-4">
          <CMLifecycleStepper state={cm.state} />
        </div>
      ) : null}

      {/* Scheduled auto-start status. */}
      {cm.autoStartMissedAt ? (
        <Alert className="mb-4 border-sev-3/40 bg-sev-3/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            This change missed its scheduled auto-start (it wasn't approved in time). Edit it
            to pick a new start time, or start it manually.
          </AlertDescription>
        </Alert>
      ) : cm.autoStart && cm.state === 'approved' && cm.startTime ? (
        <Alert className="mb-4 border-brand/30 bg-brand-soft">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            Auto-starts {formatDistanceToNow(new Date(cm.startTime), { addSuffix: true })}.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Scheduling conflicts: other changes overlapping in time on shared devices. */}
      {conflicts && conflicts.length > 0 ? (
        <Alert className="mb-4 border-sev-3/40 bg-sev-3/10">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            <span className="font-medium">Scheduling conflict.</span> This change overlaps{' '}
            {conflicts.length === 1 ? 'another change' : `${String(conflicts.length)} other changes`} on shared devices:{' '}
            {conflicts.map((c, i) => (
              <span key={c.cmId}>
                {i > 0 && ', '}
                <Link to={`/change-management/${c.cmId}`} className="underline">
                  {c.title}
                </Link>{' '}
                ({c.sharedDeviceCount} device{c.sharedDeviceCount === 1 ? '' : 's'})
              </span>
            ))}
            .
          </AlertDescription>
        </Alert>
      ) : null}

      {submitError ? (
        <Alert variant="destructive" className="mb-4 relative">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
          <button
            type="button"
            onClick={() => { setSubmitError(null); }}
            className="absolute top-2 right-2 inline-flex h-6 w-6 items-center justify-center rounded text-muted-foreground hover:bg-accent"
            aria-label="Dismiss"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Alert>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-5">
        <div className="lg:col-span-2 space-y-4 md:space-y-5">
          {/* Details */}
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Details</h2>
            <dl className="text-sm grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2.5">
              <DetailRow label="Context" value={context?.name ?? cm.contextId} />
              {cm.description ? (
                <DetailRow label="Description" value={<span className="whitespace-pre-wrap">{cm.description}</span>} />
              ) : null}
              <DetailRow
                label="Created"
                value={
                  <span>
                    {format(new Date(cm.createdAt), 'PPpp')}{' '}
                    <span className="text-muted-foreground text-xs">
                      ({formatDistanceToNow(new Date(cm.createdAt), { addSuffix: true })})
                    </span>
                  </span>
                }
              />
              {cm.startTime ? (
                <DetailRow
                  label="Scheduled start"
                  value={format(new Date(cm.startTime), 'PPpp')}
                />
              ) : null}
              {cm.expectedEndTime ? (
                <DetailRow
                  label="Expected end"
                  value={format(new Date(cm.expectedEndTime), 'PPpp')}
                />
              ) : null}
              {cm.actualEndTime ? (
                <DetailRow
                  label="Actual end"
                  value={format(new Date(cm.actualEndTime), 'PPpp')}
                />
              ) : null}
              {cm.approvalStatus ? (
                <DetailRow
                  label="Approval status"
                  value={
                    <span className="inline-flex items-center gap-2">
                      {String(cm.approvalStatus.approvedCount)} of{' '}
                      {String(cm.approvalStatus.totalApprovers)} approvals
                      {cm.approvalStatus.allApproved ? (
                        <Badge className="bg-sev-4/15 text-sev-4 border-0 text-[10px] font-normal">
                          Fully approved
                        </Badge>
                      ) : null}
                    </span>
                  }
                />
              ) : null}
              {cm.isAutomated ? (
                <DetailRow label="Automation source" value={cm.automationSource ?? 'system'} />
              ) : null}
              {cm.automationTriggeredBy ? (
                <DetailRow label="Triggered by" value={cm.automationTriggeredBy} />
              ) : null}
            </dl>
          </section>

          {/* Affected devices — blast-radius summary (from cm_affected_devices,
              snapshotted at submit) so approvers see what the change touches. */}
          {affectedDevices && affectedDevices.length > 0 ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-3">
                Affected devices ({affectedDevices.length})
              </h2>
              <ul className="flex flex-wrap gap-2">
                {affectedDevices.map((d) => (
                  <li key={d.deviceId}>
                    <Link
                      to={`/devices/${d.deviceId}`}
                      className="inline-flex items-center gap-1.5 rounded-md border border-border-subtle bg-bg-subtle px-2 py-1 text-xs hover:bg-accent"
                    >
                      <Server className="w-3 h-3 text-muted-foreground" aria-hidden="true" />
                      <span className="font-medium">{d.name ?? d.ipAddr ?? d.deviceId}</span>
                      <span className="text-muted-foreground">{deviceTypeLabel(d.deviceType)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Implementation — attached plans rendered inline so steps are built
              and executed here without bouncing to the plans page. */}
          <section className="bg-card border border-border rounded-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-semibold">Implementation ({cmPlans?.length ?? 0})</h2>
              {!cm.archivedAt &&
              (cm.state === 'in_progress' || cm.state === 'paused') &&
              cmPlans?.some((p) => p.state === 'failed') ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => { void handleRetryFailedPlans(); }}
                  disabled={retryCMMutation.isPending}
                >
                  <RefreshCw
                    className={cn('w-3.5 h-3.5', retryCMMutation.isPending && 'animate-spin')}
                  />
                  Retry failed
                </Button>
              ) : null}
            </div>

            {isCmExecuting && totalSteps > 0 ? (
              <div className="mb-4">
                <Progress value={(completedSteps / totalSteps) * 100} className="h-1.5 mb-1.5" />
                <p className="text-xs text-muted-foreground">
                  {completedSteps} of {totalSteps} steps complete
                </p>
              </div>
            ) : null}

            {cmPlans && cmPlans.length > 0 ? (
              <div className="space-y-4">
                {cmPlans.map((p) => (
                  <div key={p.planId} className="rounded-lg border border-border-subtle">
                    <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-border-subtle">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="font-mono text-xs text-muted-foreground">
                          {p.position + 1}
                        </span>
                        <Link
                          to={`/network-plans/${p.planId}`}
                          className="font-medium hover:underline truncate"
                        >
                          {p.title}
                        </Link>
                        <Badge
                          className={cn(
                            'border-0 font-normal capitalize',
                            PLAN_STATE_STYLE[p.state] ?? 'bg-muted text-muted-foreground',
                          )}
                        >
                          {p.state}
                        </Badge>
                      </div>
                      {!cm.archivedAt && cm.state === 'draft' ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive shrink-0"
                          onClick={() => { void handleDetachPlan(p.planId); }}
                        >
                          Detach
                        </Button>
                      ) : null}
                    </div>
                    <div className="p-4">
                      <PlanStepEditor planId={p.planId} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <EmptyState
                variant="no-data"
                title="No implementation plan"
                description="Add an implementation plan to define the steps this change will run."
                size="inline"
              />
            )}

            {!cm.archivedAt && cm.state === 'draft' ? (
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={() => { void handleAddImplementationPlan(); }}
                  disabled={createPlanMutation.isPending || attachPlanMutation.isPending}
                >
                  <Plus className="w-4 h-4" />
                  Add implementation plan
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setIsAttachOpen(true); }}>
                  <Link2 className="w-4 h-4" />
                  Attach existing plan
                </Button>
              </div>
            ) : null}
          </section>

          {/* Alerts that fired on this change's devices while it was active. */}
          {cmAlerts && cmAlerts.length > 0 ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-3">
                Alerts during this change ({cmAlerts.length})
              </h2>
              <ul className="divide-y divide-border-subtle">
                {cmAlerts.map((a, i) => (
                  <li key={a.dedupeKey ?? String(i)} className="py-2 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-medium truncate">
                        {a.title ?? a.dedupeKey ?? 'Alert'}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {a.deviceName ?? '—'}
                        {a.incidentStatus ? ` · incident ${a.incidentStatus}` : ''}
                        {' · '}
                        {formatDistanceToNow(new Date(a.lastSeen), { addSuffix: true })}
                      </div>
                    </div>
                    <Badge
                      className={cn(
                        'border-0 font-normal capitalize shrink-0',
                        ALERT_REL_STYLE[a.relationship] ?? 'bg-muted text-muted-foreground',
                      )}
                    >
                      {a.relationship}
                    </Badge>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* Pre/post config snapshots for affected devices. */}
          <CMSnapshotsPanel cmId={cm.id} canRollback={!cm.archivedAt} />

          {/* Workflow actions */}
          {workflowActions.length > 0 && !cm.archivedAt ? (
            <section className="bg-card border border-border rounded-lg p-5">
              <h2 className="text-sm font-semibold mb-3">Workflow</h2>
              <div className="flex flex-wrap gap-2" data-testid="cm-actions">
                {workflowActions.map((action) => (
                  <Button
                    key={action.label}
                    variant={action.variant}
                    onClick={action.onClick}
                    className={action.className}
                  >
                    <action.Icon className="w-4 h-4" />
                    {action.label}
                  </Button>
                ))}
              </div>
            </section>
          ) : null}

          {/* Activity timeline */}
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Activity timeline</h2>

            {!cm.archivedAt ? (
              <form onSubmit={(e) => { void handleAddComment(e); }} className="mb-5 space-y-2">
                <Textarea
                  value={commentText}
                  onChange={(e) => { setCommentText(e.target.value); }}
                  placeholder="Add a comment…"
                  rows={3}
                />
                <div className="flex justify-end">
                  <Button
                    type="submit"
                    size="sm"
                    disabled={!commentText.trim() || addCommentMutation.isPending}
                  >
                    <MessageSquare className="w-4 h-4" />
                    {addCommentMutation.isPending ? 'Adding…' : 'Add comment'}
                  </Button>
                </div>
              </form>
            ) : null}

            {eventsLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : !events || events.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity yet.</p>
            ) : (
              <ol className="relative space-y-4 pl-6 before:absolute before:left-3 before:top-1.5 before:bottom-1.5 before:w-px before:bg-border-subtle">
                {events.map((event) => {
                  const Icon = eventIcon(event.eventType);
                  const desc = describeCMEvent(event);
                  return (
                    <li key={event.id} className="relative">
                      <span className="absolute -left-6 top-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-bg-subtle border border-border text-muted-foreground">
                        <Icon className="w-3 h-3" aria-hidden="true" />
                      </span>
                      <div className="text-[11px]">
                        <span className="font-medium text-foreground">
                          {event.createdByName ?? 'System'}
                        </span>
                        <span className="text-muted-foreground">
                          {' · '}
                          {formatDistanceToNow(new Date(event.createdAt), { addSuffix: true })}
                        </span>
                      </div>
                      <div className="text-sm font-medium">{desc.title}</div>
                      {desc.detail ? (
                        <p
                          className={cn(
                            'text-sm mt-1 whitespace-pre-wrap',
                            // A warning about something missing from this change, or
                            // holding its start, has to survive a skim; every other
                            // detail here reports something that did happen.
                            event.eventType === 'remediation_blocked' || event.eventType === 'auto_start_held'
                              ? 'text-sev-2 bg-sev-2/10 border border-sev-2/20 rounded px-2 py-1.5'
                              : 'text-foreground'
                          )}
                        >
                          {desc.detail}
                        </p>
                      ) : null}
                    </li>
                  );
                })}
              </ol>
            )}
          </section>
        </div>

        {/* Sidecar */}
        <aside>
          <section className="bg-card border border-border rounded-lg p-5">
            <h2 className="text-sm font-semibold mb-4">Participants</h2>
            {id ? <ParticipantsPanel cmId={id} /> : null}
          </section>
        </aside>
      </div>

      {/* Edit AppSheet */}
      <AppSheet open={isEditOpen} onOpenChange={setIsEditOpen} title="Edit change management">
        <AppSheet.Body>
          <form
            id="cm-edit-form"
            onSubmit={(e) => { void handleEditSubmit(e); }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cm-edit-title">Title</Label>
              <Input
                id="cm-edit-title"
                value={editTitle}
                onChange={(e) => { setEditTitle(e.target.value); }}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cm-edit-description">Description</Label>
              <Textarea
                id="cm-edit-description"
                value={editDescription}
                onChange={(e) => { setEditDescription(e.target.value); }}
                rows={6}
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="cm-edit-start">Scheduled start</Label>
                <Input
                  id="cm-edit-start"
                  type="datetime-local"
                  value={editStartTime}
                  onChange={(e) => { setEditStartTime(e.target.value); }}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cm-edit-end">Expected end</Label>
                <Input
                  id="cm-edit-end"
                  type="datetime-local"
                  value={editExpectedEndTime}
                  onChange={(e) => { setEditExpectedEndTime(e.target.value); }}
                />
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <Checkbox
                checked={editSuppressAlerts}
                onCheckedChange={(v) => { setEditSuppressAlerts(v === true); }}
              />
              <span>Suppress alerts during this change</span>
            </label>
            {cmSettings?.autoStartEnabled ? (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <Checkbox
                  checked={editAutoStart}
                  onCheckedChange={(v) => { setEditAutoStart(v === true); }}
                />
                <span>Start automatically at the scheduled time once approved</span>
              </label>
            ) : null}
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsEditOpen(false); }}
            disabled={updateMutation.isPending}
          >
            Cancel
          </Button>
          <Button type="submit" form="cm-edit-form" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving…' : 'Save changes'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>

      {/* Attach plan AppSheet */}
      <AppSheet open={isAttachOpen} onOpenChange={setIsAttachOpen} title="Attach plan">
        <AppSheet.Body>
          <form
            id="cm-attach-form"
            onSubmit={(e) => { void handleAttachPlan(e); }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cm-attach-plan">Select a draft plan</Label>
              <Select value={attachPlanId} onValueChange={setAttachPlanId}>
                <SelectTrigger id="cm-attach-plan">
                  <SelectValue placeholder="Choose a plan…" />
                </SelectTrigger>
                <SelectContent>
                  {availablePlans
                    ?.filter((p) => !cmPlans?.some((cp) => cp.planId === p.id))
                    .map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.title}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </form>
        </AppSheet.Body>
        <AppSheet.Footer>
          <Button
            variant="outline"
            onClick={() => { setIsAttachOpen(false); }}
            disabled={attachPlanMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="cm-attach-form"
            disabled={!attachPlanId || attachPlanMutation.isPending}
          >
            {attachPlanMutation.isPending ? 'Attaching…' : 'Attach'}
          </Button>
        </AppSheet.Footer>
      </AppSheet>
      {confirmDialog}
    </PageContainer>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <>
      <dt className="text-muted-foreground font-medium">{label}</dt>
      <dd className="break-words min-w-0">{value}</dd>
    </>
  );
}
